/**
 * NotificationPreferencesService - User notification preference management with Firestore persistence
 *
 * Boundary: Does not send notifications; manages preference schemas and storage only
 * External dependencies: Firestore 'user-notification-settings' collection, UserService.getUserModel(), localStorage keys 'scan_ninja_notification_preferences_${userId}'
 */

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggingService } from './logging.service';
import { UserService } from './user.service';
import {
  UserNotificationPreferences,
  AlertType,
  AlertSeverity,
  NotificationChannel,
} from './alert-notification.service';
import { NotificationSettingsService } from './notification-settings.service';
import {
  UserNotificationSettings,
  NotificationType,
} from '@core/models/notification.types';
import { SafeStorageService } from './safe-storage.service';

/**
 * Manages user notification preferences with role-based defaults, Firebase persistence, and bidirectional format conversion
 *
 * Dependencies:
 * - LoggingService: Error and warning logging
 * - NotificationSettingsService: Firestore CRUD for UserNotificationSettings
 * - UserService: Current user document retrieval for contact info population
 *
 * Side effects:
 * - Reads/writes localStorage keys: 'scan_ninja_notification_preferences_${userId}'
 * - Reads/writes Firestore via NotificationSettingsService
 * - BehaviorSubject: preferences$ (emits UserNotificationPreferences on load/update)
 * - Automatic migration from legacy notification settings on first load
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationPreferencesService {
  private loggingService = inject(LoggingService);
  private notificationSettingsService = inject(NotificationSettingsService);
  private userService = inject(UserService);
  private safeStorage = inject(SafeStorageService);

  private preferences$ =
    new BehaviorSubject<UserNotificationPreferences | null>(null);
  private readonly storageKey = 'scan_ninja_notification_preferences';

  // Default preference templates for different user roles
  private readonly defaultPreferences: {
    [role: string]: Partial<UserNotificationPreferences>;
  } = {
    admin: {
      enableDesktopNotifications: true,
      enableInAppNotifications: true,
      enableEmailNotifications: true,
      enableSmsNotifications: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '08:00',
      preferences: {
        [AlertType.VULNERABILITY_SCAN]: {
          [AlertSeverity.CRITICAL]: [
            NotificationChannel.EMAIL,
            NotificationChannel.SMS,
            NotificationChannel.DESKTOP,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.HIGH]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.MEDIUM]: [
            NotificationChannel.DESKTOP,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
          [AlertSeverity.INFO]: [NotificationChannel.IN_APP],
        },
        [AlertType.SCAN_COMPLETION]: {
          [AlertSeverity.INFO]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
            NotificationChannel.IN_APP,
          ],
        },
        [AlertType.TICKET_UPDATE]: {
          [AlertSeverity.HIGH]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
          ],
          [AlertSeverity.MEDIUM]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.LOW]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.INFO]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
        },
        [AlertType.COMPLIANCE_DRIFT]: {
          [AlertSeverity.CRITICAL]: [
            NotificationChannel.EMAIL,
            NotificationChannel.SMS,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.HIGH]: [
            NotificationChannel.EMAIL,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.MEDIUM]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
        },
        [AlertType.INCIDENT_RESPONSE]: {
          [AlertSeverity.CRITICAL]: [
            NotificationChannel.EMAIL,
            NotificationChannel.SMS,
            NotificationChannel.DESKTOP,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.HIGH]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
            NotificationChannel.SIGNALR,
          ],
          [AlertSeverity.MEDIUM]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
          ],
          [AlertSeverity.LOW]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
        },
      },
    },
    user: {
      enableDesktopNotifications: true,
      enableInAppNotifications: true,
      enableEmailNotifications: false,
      enableSmsNotifications: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '08:00',
      preferences: {
        [AlertType.VULNERABILITY_SCAN]: {
          [AlertSeverity.CRITICAL]: [
            NotificationChannel.DESKTOP,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.HIGH]: [
            NotificationChannel.DESKTOP,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.MEDIUM]: [NotificationChannel.IN_APP],
          [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
          [AlertSeverity.INFO]: [NotificationChannel.IN_APP],
        },
        [AlertType.SCAN_COMPLETION]: {
          [AlertSeverity.INFO]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
            NotificationChannel.IN_APP,
          ],
        },
        [AlertType.TICKET_UPDATE]: {
          [AlertSeverity.HIGH]: [
            NotificationChannel.EMAIL,
            NotificationChannel.DESKTOP,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.MEDIUM]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.LOW]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
          [AlertSeverity.INFO]: [
            NotificationChannel.EMAIL,
            NotificationChannel.IN_APP,
          ],
        },
      },
    },
    readonly: {
      enableDesktopNotifications: false,
      enableInAppNotifications: true,
      enableEmailNotifications: false,
      enableSmsNotifications: false,
      preferences: {
        [AlertType.SCAN_COMPLETION]: {
          [AlertSeverity.INFO]: [NotificationChannel.IN_APP],
        },
        [AlertType.TICKET_UPDATE]: {
          [AlertSeverity.HIGH]: [NotificationChannel.IN_APP],
          [AlertSeverity.MEDIUM]: [NotificationChannel.IN_APP],
          [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
          [AlertSeverity.INFO]: [NotificationChannel.IN_APP],
        },
        [AlertType.VULNERABILITY_SCAN]: {
          [AlertSeverity.CRITICAL]: [NotificationChannel.IN_APP],
          [AlertSeverity.HIGH]: [NotificationChannel.IN_APP],
          [AlertSeverity.MEDIUM]: [NotificationChannel.IN_APP],
          [AlertSeverity.LOW]: [NotificationChannel.IN_APP],
          [AlertSeverity.INFO]: [NotificationChannel.IN_APP],
        },
      },
    },
  };

  /**
   * Initializes notification preferences for a user with role-based defaults and optional migration
   *
   * @param userId - User identifier
   * @param userRole - User role for default templates ('admin', 'user', 'readonly'; default 'user')
   * @returns Promise<UserNotificationPreferences> created or loaded preferences
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns fallback defaults on failure
   * - State touched: Firestore read/write via NotificationSettingsService, localStorage read/write, preferences$ BehaviorSubject emission
   * - Idempotency: Safe to call multiple times; loads existing if present, creates only if missing
   *
   * @example
   * // Call on user login or settings page initialization
   */
  async initializePreferences(
    userId: string,
    userRole: string = 'user',
  ): Promise<UserNotificationPreferences> {
    try {
      // Try to load existing preferences
      let preferences = await this.loadPreferences(userId);

      if (!preferences) {
        // Attempt to migrate from legacy notification service
        await this.migrateFromLegacyNotifications(userId);
        preferences = await this.loadPreferences(userId);
      }

      if (!preferences) {
        // Create default preferences based on user role
        preferences = this.createDefaultPreferences(userId, userRole);

        // Try to populate contact information from user profile
        try {
          await this.populateContactInfoFromProfile(preferences, userId);
        } catch (error) {
          this.loggingService.logError(
            'Failed to populate contact info from profile',
            error,
          );
          // Continue without contact info
        }

        await this.savePreferences(preferences);
      } else {
        // Sync local storage with Firebase if needed
        await this.syncPreferences(userId);
      }

      this.preferences$.next(preferences);

      return preferences;
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to initialize notification preferences:',
        error,
      );

      // Fallback to default preferences
      const fallback = this.createDefaultPreferences(userId, userRole);
      this.preferences$.next(fallback);
      return fallback;
    }
  }

  /**
   * Retrieves current user preferences as observable stream
   *
   * @returns Observable<UserNotificationPreferences | null> emits on preference load/update or null if not initialized
   * - Observable semantics: BehaviorSubject replay; emits current value immediately on subscribe
   * - HTTP errors: N/A
   * - State touched: None (read-only accessor)
   * - Idempotency: Safe to call multiple times; same observable instance
   */
  getPreferences(): Observable<UserNotificationPreferences | null> {
    return this.preferences$.asObservable();
  }

  /**
   * Retrieves current user preferences synchronously (snapshot)
   *
   * @returns UserNotificationPreferences | null current cached preferences or null if not initialized
   * - Observable semantics: N/A (synchronous)
   * - HTTP errors: N/A
   * - State touched: None (read-only accessor)
   * - Idempotency: Safe to call multiple times
   */
  getCurrentPreferences(): UserNotificationPreferences | null {
    return this.preferences$.value;
  }

  /**
   * Updates notification preferences (partial merge)
   *
   * @param updates - Partial preference object to merge with current preferences
   * @returns Promise<boolean> true on successful save, false on validation failure or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Firestore write via NotificationSettingsService, localStorage write, preferences$ BehaviorSubject emission
   * - Idempotency: Safe to call multiple times with same updates; updates timestamp each time
   *
   * @example
   * // Update email notifications only
   * await service.updatePreferences({ enableEmailNotifications: true });
   */
  async updatePreferences(
    updates: Partial<UserNotificationPreferences>,
  ): Promise<boolean> {
    try {
      const current = this.preferences$.value;
      if (!current) {
        throw new Error('No current preferences loaded');
      }

      // Merge updates with current preferences
      const updated: UserNotificationPreferences = {
        ...current,
        ...updates,
        preferences: {
          ...current.preferences,
          ...updates.preferences,
        },
      };

      // Validate the updated preferences
      if (!this.validatePreferences(updated)) {
        throw new Error('Invalid preference configuration');
      }

      // Save and update
      await this.savePreferences(updated);
      this.preferences$.next(updated);

      return true;
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to update notification preferences:',
        error,
      );
      return false;
    }
  }

  /**
   * Updates channel preferences for specific alert type and severity levels
   *
   * @param alertType - Alert type to configure (e.g., AlertType.VULNERABILITY_SCAN)
   * @param severityPreferences - Map of severity levels to notification channels
   * @returns Promise<boolean> true on successful save, false on error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Delegates to updatePreferences; same side effects
   * - Idempotency: Safe to call multiple times
   */
  async updateAlertTypePreferences(
    alertType: AlertType,
    severityPreferences: {
      [severity in AlertSeverity]?: NotificationChannel[];
    },
  ): Promise<boolean> {
    try {
      const current = this.preferences$.value;
      if (!current) {
        throw new Error('No current preferences loaded');
      }

      const updatedPreferences = {
        ...current.preferences,
        [alertType]: {
          ...current.preferences[alertType],
          ...severityPreferences,
        },
      };

      return await this.updatePreferences({ preferences: updatedPreferences });
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to update alert type preferences:',
        error,
      );
      return false;
    }
  }

  /**
   * Enables or disables a notification channel globally (across all alert types)
   *
   * @param channel - Notification channel (DESKTOP, IN_APP, EMAIL, SMS)
   * @param enabled - true to enable, false to disable
   * @returns Promise<boolean> true on successful save, false on error or unsupported channel
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Delegates to updatePreferences; same side effects
   * - Idempotency: Safe to call multiple times with same state
   */
  async updateChannelStatus(
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<boolean> {
    try {
      const updates: Partial<UserNotificationPreferences> = {};

      switch (channel) {
        case NotificationChannel.DESKTOP:
          updates.enableDesktopNotifications = enabled;
          break;
        case NotificationChannel.IN_APP:
          updates.enableInAppNotifications = enabled;
          break;
        case NotificationChannel.EMAIL:
          updates.enableEmailNotifications = enabled;
          break;
        case NotificationChannel.SMS:
          updates.enableSmsNotifications = enabled;
          break;
        default:
          this.loggingService.logWarning(
            '⚠️ Cannot globally enable/disable channel:',
            channel,
          );
          return false;
      }

      return await this.updatePreferences(updates);
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to update channel status:',
        error,
      );
      return false;
    }
  }

  /**
   * Configures quiet hours time range with optional timezone
   *
   * @param startTime - Start time in HH:MM format (24-hour)
   * @param endTime - End time in HH:MM format (24-hour; can be before startTime for overnight ranges)
   * @param timezone - Optional IANA timezone identifier (e.g., 'America/New_York')
   * @returns Promise<boolean> true on successful save, false on invalid time format or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Delegates to updatePreferences; same side effects
   * - Idempotency: Safe to call multiple times with same times
   */
  async setQuietHours(
    startTime: string,
    endTime: string,
    timezone?: string,
  ): Promise<boolean> {
    try {
      // Validate time format (HH:MM)
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
        throw new Error('Invalid time format. Use HH:MM format.');
      }

      const updates: Partial<UserNotificationPreferences> = {
        quietHoursStart: startTime,
        quietHoursEnd: endTime,
      };

      if (timezone) {
        updates.timezone = timezone;
      }

      return await this.updatePreferences(updates);
    } catch (error) {
      this.loggingService.logError('❌ Failed to set quiet hours:', error);
      return false;
    }
  }

  /**
   * Updates user contact information with validation
   *
   * @param email - Optional email address (validated with regex)
   * @param phone - Optional phone number (validated with regex; supports international formats)
   * @returns Promise<boolean> true on successful save, false on validation failure or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Delegates to updatePreferences; same side effects
   * - Idempotency: Safe to call multiple times with same contact info
   */
  async updateContactInfo(email?: string, phone?: string): Promise<boolean> {
    try {
      const updates: Partial<UserNotificationPreferences> = {};

      if (email !== undefined) {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (email && !emailRegex.test(email)) {
          throw new Error('Invalid email format');
        }
        updates.email = email;
      }

      if (phone !== undefined) {
        // Basic phone validation (optional formatting)
        if (
          phone &&
          !/^[+]?[1-9][\d]{0,15}$/.test(phone.replace(/[\s-()]/g, ''))
        ) {
          throw new Error('Invalid phone number format');
        }
        updates.phone = phone;
      }

      return await this.updatePreferences(updates);
    } catch (error) {
      this.loggingService.logError('❌ Failed to update contact info:', error);
      return false;
    }
  }

  /**
   * Resets preferences to role-based defaults while preserving contact information
   *
   * @param userRole - User role for default templates ('admin', 'user', 'readonly'; default 'user')
   * @returns Promise<boolean> true on successful reset, false on error or if no current preferences loaded
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Firestore write via NotificationSettingsService, localStorage write, preferences$ BehaviorSubject emission
   * - Idempotency: Safe to call multiple times; always resets to defaults
   */
  async resetToDefaults(userRole: string = 'user'): Promise<boolean> {
    try {
      const current = this.preferences$.value;
      if (!current) {
        throw new Error('No current preferences loaded');
      }

      const defaults = this.createDefaultPreferences(current.userId, userRole);

      // Preserve contact information
      defaults.email = current.email;
      defaults.phone = current.phone;

      await this.savePreferences(defaults);
      this.preferences$.next(defaults);

      return true;
    } catch (error) {
      this.loggingService.logError('❌ Failed to reset preferences:', error);
      return false;
    }
  }

  /**
   * Exports current preferences as JSON string (for backup or debugging)
   *
   * @returns JSON string of current preferences or null if not loaded
   * - Observable semantics: N/A (synchronous)
   * - HTTP errors: N/A
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  exportPreferences(): string | null {
    try {
      const preferences = this.preferences$.value;
      if (!preferences) {
        throw new Error('No preferences to export');
      }

      return JSON.stringify(preferences, null, 2);
    } catch (error) {
      this.loggingService.logError('❌ Failed to export preferences:', error);
      return null;
    }
  }

  /**
   * Imports preferences from JSON string with validation
   *
   * @param preferencesJson - JSON string of UserNotificationPreferences object
   * @returns Promise<boolean> true on successful import and save, false on invalid JSON or validation failure
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Firestore write via NotificationSettingsService, localStorage write, preferences$ BehaviorSubject emission
   * - Idempotency: Safe to call multiple times with same JSON; overwrites existing preferences
   */
  async importPreferences(preferencesJson: string): Promise<boolean> {
    try {
      const preferences = JSON.parse(
        preferencesJson,
      ) as UserNotificationPreferences;

      if (!this.validatePreferences(preferences)) {
        throw new Error('Invalid preferences format');
      }

      await this.savePreferences(preferences);
      this.preferences$.next(preferences);

      return true;
    } catch (error) {
      this.loggingService.logError('❌ Failed to import preferences:', error);
      return false;
    }
  }

  /**
   * Create default preferences for a user
   */
  private createDefaultPreferences(
    userId: string,
    userRole: string,
  ): UserNotificationPreferences {
    const roleDefaults =
      this.defaultPreferences[userRole] || this.defaultPreferences['user'];

    // No hardcoded demo data - let users provide their own contact information
    // Contact info will be undefined until user updates their preferences
    const defaultEmail = undefined;
    const defaultPhone = undefined;

    return {
      userId,
      email: defaultEmail,
      phone: defaultPhone,
      enableDesktopNotifications: true,
      enableInAppNotifications: true,
      enableEmailNotifications: false, // Disabled until user provides email
      enableSmsNotifications: false, // Disabled until user provides phone
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...roleDefaults,
      preferences: roleDefaults.preferences || {},
    } as UserNotificationPreferences;
  }

  /**
   * Populate contact information from user profile
   */
  private async populateContactInfoFromProfile(
    preferences: UserNotificationPreferences,
    userId: string,
  ): Promise<void> {
    try {
      // Get user document from Firestore
      const userDoc = await this.userService.getDocument(userId);

      if (userDoc) {
        // Update email if available in profile and not already set in preferences
        if (userDoc.email && !preferences.email) {
          preferences.email = userDoc.email;
          preferences.enableEmailNotifications = true;
        }

        // Update phone if available in profile and not already set in preferences
        if (userDoc.phone && !preferences.phone) {
          preferences.phone = userDoc.phone;
          preferences.enableSmsNotifications = true;
        }
      }
    } catch (error) {
      this.loggingService.logError(
        'Failed to populate contact info from profile:',
        error,
      );
      // Don't throw - this is optional enhancement
    }
  }

  /**
   * Load preferences from storage
   */
  private async loadPreferences(
    userId: string,
  ): Promise<UserNotificationPreferences | null> {
    try {
      // Load from Firebase/Firestore first
      const firebaseSettings =
        await this.notificationSettingsService.getUserNotificationSettings(
          userId,
        );
      if (firebaseSettings) {
        // Convert Firebase settings to UserNotificationPreferences format
        const preferences = this.convertFirebaseToPreferences(firebaseSettings);

        return preferences;
      }

      // Fallback to local storage
      const stored = this.safeStorage.getItem(`${this.storageKey}_${userId}`);
      if (stored) {
        const preferences = JSON.parse(stored) as UserNotificationPreferences;

        if (this.validatePreferences(preferences)) {
          // Migrate to Firebase for future use
          await this.savePreferences(preferences);

          return preferences;
        }
      }

      return null;
    } catch (error) {
      this.loggingService.logWarning(
        '⚠️ Failed to load preferences from storage:',
        error,
      );
      return null;
    }
  }

  /**
   * Save preferences to storage
   */
  private async savePreferences(
    preferences: UserNotificationPreferences,
  ): Promise<void> {
    try {
      // Save to local storage as backup
      this.safeStorage.setItem(
        `${this.storageKey}_${preferences.userId}`,
        JSON.stringify(preferences),
      );

      // Save to Firebase/Firestore as primary storage
      const firebaseSettings = this.convertPreferencesToFirebase(preferences);
      await this.notificationSettingsService.saveUserNotificationSettings(
        firebaseSettings,
      );
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to save preferences to storage:',
        error,
      );
      throw error;
    }
  }

  /**
   * Validate preferences structure
   */
  private validatePreferences(
    preferences: any,
  ): preferences is UserNotificationPreferences {
    if (!preferences || typeof preferences !== 'object') {
      return false;
    }

    // Check required fields
    if (!preferences.userId || typeof preferences.userId !== 'string') {
      return false;
    }

    // Check boolean fields
    const booleanFields = [
      'enableDesktopNotifications',
      'enableInAppNotifications',
      'enableEmailNotifications',
      'enableSmsNotifications',
    ];
    for (const field of booleanFields) {
      if (
        preferences[field] !== undefined &&
        typeof preferences[field] !== 'boolean'
      ) {
        return false;
      }
    }

    // Check preferences structure
    if (
      preferences.preferences &&
      typeof preferences.preferences !== 'object'
    ) {
      return false;
    }

    return true;
  }

  /**
   * Generates human-readable preference summary for UI display
   *
   * @returns Object with enabled channels, alert type counts, quiet hours range, and contact method availability or null if not loaded
   * - Observable semantics: N/A (synchronous)
   * - HTTP errors: N/A
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  getPreferencesSummary(): {
    channels: { [channel: string]: boolean };
    alertTypes: { [alertType: string]: number };
    quietHours: string | null;
    contactMethods: { email: boolean; sms: boolean };
  } | null {
    const preferences = this.preferences$.value;
    if (!preferences) return null;

    const channels = {
      desktop: preferences.enableDesktopNotifications,
      inApp: preferences.enableInAppNotifications,
      email: preferences.enableEmailNotifications,
      sms: preferences.enableSmsNotifications,
    };

    const alertTypes: { [key: string]: number } = {};
    Object.keys(preferences.preferences).forEach((alertType) => {
      const typePrefs = preferences.preferences[alertType as AlertType];
      if (typePrefs) {
        alertTypes[alertType] = Object.keys(typePrefs).length;
      }
    });

    const quietHours =
      preferences.quietHoursStart && preferences.quietHoursEnd
        ? `${preferences.quietHoursStart} - ${preferences.quietHoursEnd}`
        : null;

    const contactMethods = {
      email: !!(preferences.email && preferences.enableEmailNotifications),
      sms: !!(preferences.phone && preferences.enableSmsNotifications),
    };

    return {
      channels,
      alertTypes,
      quietHours,
      contactMethods,
    };
  }

  /**
   * Convert Firebase UserNotificationSettings to UserNotificationPreferences
   */
  private convertFirebaseToPreferences(
    firebaseSettings: UserNotificationSettings,
  ): UserNotificationPreferences {
    // Map Firebase notification types to alert types and channels
    const preferences: UserNotificationPreferences['preferences'] = {};

    // Initialize with default structure
    Object.values(AlertType).forEach((alertType) => {
      preferences[alertType] = {};
      Object.values(AlertSeverity).forEach((severity) => {
        preferences[alertType]![severity] = [];
      });
    });

    // Map Firebase preferences to our preference structure
    if (
      firebaseSettings.preferences &&
      Array.isArray(firebaseSettings.preferences)
    ) {
      firebaseSettings.preferences.forEach((pref) => {
        if (pref.enabled) {
          const channels: NotificationChannel[] = [];

          if (pref.email && firebaseSettings.globalEnabled) {
            channels.push(NotificationChannel.EMAIL);
          }
          if (pref.push && firebaseSettings.globalEnabled) {
            channels.push(NotificationChannel.DESKTOP);
          }
          if (pref.sms && firebaseSettings.globalEnabled) {
            channels.push(NotificationChannel.SMS);
          }

          // Map notification types to alert types
          switch (pref.type) {
            case NotificationType.SECURITY_ALERTS:
            case NotificationType.CRITICAL_VULNERABILITIES:
              if (preferences[AlertType.VULNERABILITY_SCAN]) {
                preferences[AlertType.VULNERABILITY_SCAN][
                  AlertSeverity.CRITICAL
                ] = channels;
                preferences[AlertType.VULNERABILITY_SCAN][AlertSeverity.HIGH] =
                  channels;
              }
              break;
            case NotificationType.SCAN_COMPLETION:
              if (preferences[AlertType.SCAN_COMPLETION]) {
                preferences[AlertType.SCAN_COMPLETION][AlertSeverity.INFO] =
                  channels;
              }
              break;
            case NotificationType.COMPLIANCE_ALERTS:
              if (preferences[AlertType.COMPLIANCE_DRIFT]) {
                preferences[AlertType.COMPLIANCE_DRIFT][AlertSeverity.HIGH] =
                  channels;
                preferences[AlertType.COMPLIANCE_DRIFT][AlertSeverity.MEDIUM] =
                  channels;
              }
              break;
            case NotificationType.FAILED_SCANS:
              if (preferences[AlertType.SCAN_COMPLETION]) {
                preferences[AlertType.SCAN_COMPLETION][AlertSeverity.HIGH] =
                  channels;
              }
              break;
            case NotificationType.REMEDIATION_UPDATES:
              if (preferences[AlertType.TICKET_UPDATE]) {
                preferences[AlertType.TICKET_UPDATE][AlertSeverity.MEDIUM] =
                  channels;
              }
              break;
            case NotificationType.SYSTEM_MAINTENANCE:
              if (preferences[AlertType.SYSTEM_STATUS]) {
                preferences[AlertType.SYSTEM_STATUS][AlertSeverity.INFO] =
                  channels;
              }
              break;
          }
        }
      });
    }

    return {
      userId: firebaseSettings.userId,
      email: firebaseSettings.email,
      phone: firebaseSettings.smsNumber || undefined,
      preferences,
      enableDesktopNotifications: firebaseSettings.globalEnabled ?? true,
      enableInAppNotifications: true, // Always enabled for in-app
      enableEmailNotifications: firebaseSettings.globalEnabled ?? false,
      enableSmsNotifications:
        (firebaseSettings.globalEnabled ?? false) &&
        !!firebaseSettings.smsNumber,
      quietHoursStart: firebaseSettings.quietHours?.enabled
        ? firebaseSettings.quietHours.startTime
        : undefined,
      quietHoursEnd: firebaseSettings.quietHours?.enabled
        ? firebaseSettings.quietHours.endTime
        : undefined,
      timezone: firebaseSettings.timezone,
    };
  }

  /**
   * Convert UserNotificationPreferences to Firebase UserNotificationSettings
   */
  private convertPreferencesToFirebase(
    preferences: UserNotificationPreferences,
  ): UserNotificationSettings {
    // Create notification preferences based on alert preferences
    const notificationPreferences: Array<{
      type: NotificationType;
      email: boolean;
      push: boolean;
      sms: boolean;
      enabled: boolean;
    }> = [];

    // Map each alert type to corresponding notification types
    const alertToNotificationMapping: {
      [key in AlertType]?: NotificationType[];
    } = {
      [AlertType.VULNERABILITY_SCAN]: [
        NotificationType.SECURITY_ALERTS,
        NotificationType.CRITICAL_VULNERABILITIES,
      ],
      [AlertType.SCAN_COMPLETION]: [NotificationType.SCAN_COMPLETION],
      [AlertType.COMPLIANCE_DRIFT]: [NotificationType.COMPLIANCE_ALERTS],
      [AlertType.TICKET_UPDATE]: [NotificationType.REMEDIATION_UPDATES],
      [AlertType.SYSTEM_STATUS]: [NotificationType.SYSTEM_MAINTENANCE],
      [AlertType.INCIDENT_RESPONSE]: [NotificationType.SECURITY_ALERTS],
      [AlertType.WEEKLY_REPORT]: [NotificationType.PLATFORM_UPDATES],
    };

    // Process each alert type
    Object.entries(preferences.preferences).forEach(
      ([alertType, severityMap]) => {
        const notificationTypes =
          alertToNotificationMapping[alertType as AlertType] || [];

        notificationTypes.forEach((notificationType: NotificationType) => {
          // Determine if any severity level has notifications enabled
          const hasEmailNotifications = Object.values(severityMap || {}).some(
            (channels) => channels?.includes(NotificationChannel.EMAIL),
          );
          const hasDesktopNotifications = Object.values(severityMap || {}).some(
            (channels) => channels?.includes(NotificationChannel.DESKTOP),
          );
          const hasSmsNotifications = Object.values(severityMap || {}).some(
            (channels) => channels?.includes(NotificationChannel.SMS),
          );

          notificationPreferences.push({
            type: notificationType,
            email:
              hasEmailNotifications && preferences.enableEmailNotifications,
            push:
              hasDesktopNotifications && preferences.enableDesktopNotifications,
            sms: hasSmsNotifications && preferences.enableSmsNotifications,
            enabled:
              hasEmailNotifications ||
              hasDesktopNotifications ||
              hasSmsNotifications,
          });
        });
      },
    );

    // Add default notification types if not present
    const existingTypes = new Set(notificationPreferences.map((p) => p.type));
    Object.values(NotificationType).forEach((type) => {
      if (!existingTypes.has(type)) {
        notificationPreferences.push({
          type,
          email: false,
          push: false,
          sms: false,
          enabled: false,
        });
      }
    });

    return {
      userId: preferences.userId,
      email: preferences.email || '',
      smsNumber: preferences.phone || '',
      globalEnabled:
        preferences.enableEmailNotifications ||
        preferences.enableDesktopNotifications ||
        preferences.enableSmsNotifications,
      weeklyReports: true, // Default value
      threatIntelUpdates: false, // Default value
      quietHours: {
        enabled: !!(preferences.quietHoursStart && preferences.quietHoursEnd),
        startTime: preferences.quietHoursStart || '22:00',
        endTime: preferences.quietHoursEnd || '08:00',
      },
      timezone:
        preferences.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      weekendsOnly: false, // Default value
      preferences: notificationPreferences,
      updatedAt: new Date(),
    };
  }

  /**
   * Syncs local storage preferences to Firestore if local is newer (bidirectional sync helper)
   *
   * @param userId - User identifier
   * @returns Promise<boolean> true on successful sync or if no sync needed, false on error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Firestore write via NotificationSettingsService if local is newer
   * - Idempotency: Safe to call multiple times; only writes if local is newer than Firestore
   */
  async syncPreferences(userId: string): Promise<boolean> {
    try {
      const localData = this.safeStorage.getItem(`${this.storageKey}_${userId}`);
      if (!localData) {
        return true; // Nothing to sync
      }

      const localPreferences = JSON.parse(
        localData,
      ) as UserNotificationPreferences;
      const firebaseSettings =
        await this.notificationSettingsService.getUserNotificationSettings(
          userId,
        );

      // If Firebase doesn't have settings or local is newer, sync to Firebase
      if (
        !firebaseSettings ||
        !firebaseSettings.updatedAt ||
        (localPreferences as any).updatedAt > firebaseSettings.updatedAt
      ) {
        await this.savePreferences(localPreferences);
        return true;
      }

      return true;
    } catch (error) {
      this.loggingService.logError('❌ Failed to sync preferences:', error);
      return false;
    }
  }

  /**
   * Clears all preferences from localStorage and Firestore (resets to defaults)
   *
   * @param userId - User identifier
   * @returns Promise<boolean> true on successful clear, false on error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: localStorage removal, Firestore write (default preferences), preferences$ BehaviorSubject set to null
   * - Idempotency: Safe to call multiple times
   */
  async clearPreferences(userId: string): Promise<boolean> {
    try {
      // Clear local storage
      this.safeStorage.removeItem(`${this.storageKey}_${userId}`);

      // Clear from Firebase by setting to defaults
      const defaultPrefs = this.createDefaultPreferences(userId, 'user');
      await this.savePreferences(defaultPrefs);

      // Update current state
      this.preferences$.next(null);

      return true;
    } catch (error) {
      this.loggingService.logError('❌ Failed to clear preferences:', error);
      return false;
    }
  }

  /**
   * Migrates legacy notification settings to new preference format (one-time migration)
   *
   * @param userId - User identifier
   * @returns Promise<boolean> true if migration completed or not needed, false on error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: Firestore read/write via NotificationSettingsService, preferences$ BehaviorSubject emission
   * - Idempotency: Safe to call multiple times; no-op if new preferences already exist
   *
   * @example
   * // Called automatically by initializePreferences if no preferences found
   */
  async migrateFromLegacyNotifications(userId: string): Promise<boolean> {
    try {
      // Check if user already has new preferences
      const existingPrefs = await this.loadPreferences(userId);
      if (existingPrefs) {
        return true;
      }

      // Load legacy notification settings
      const legacySettings =
        await this.notificationSettingsService.getUserNotificationSettings(
          userId,
        );
      if (!legacySettings) {
        return true;
      }

      // Convert and save as new preferences
      const newPreferences = this.convertFirebaseToPreferences(legacySettings);
      await this.savePreferences(newPreferences);
      this.preferences$.next(newPreferences);

      return true;
    } catch (error) {
      this.loggingService.logError(
        '❌ Failed to migrate legacy notification settings:',
        error,
      );
      return false;
    }
  }

  /**
   * Performs Firebase/Firestore integration health check (read/write test)
   *
   * @returns Promise<object> with isConnected, canRead, canWrite flags and optional error message
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns error details in result object
   * - State touched: Firestore read/write test operations; creates test record that may not be cleaned up
   * - Idempotency: Safe to call multiple times; creates new test records each time
   *
   * @example
   * // Use for diagnostics or startup health check
   * const health = await service.checkFirebaseIntegration();
   * if (!health.isConnected) console.error('Firebase unreachable');
   */
  async checkFirebaseIntegration(): Promise<{
    isConnected: boolean;
    canRead: boolean;
    canWrite: boolean;
    error?: string;
  }> {
    try {
      // Test read operation
      const testUserId = 'health-check-' + Date.now();
      let canRead = false;
      let canWrite = false;

      try {
        const _settings =
          await this.notificationSettingsService.getUserNotificationSettings(
            testUserId,
          );
        canRead = true;
      } catch (readError) {
        this.loggingService.logWarning('Firebase read test failed:', readError);
      }

      // Test write operation
      try {
        const testSettings =
          await this.notificationSettingsService.createDefaultSettings(
            testUserId,
            'test@example.com',
          );
        canWrite = true;

        // Clean up test data
        if (testSettings.uid) {
          // Note: NotificationService doesn't expose delete method, so we leave the test record
        }
      } catch (writeError) {
        this.loggingService.logWarning(
          'Firebase write test failed:',
          writeError,
        );
      }

      const isConnected = canRead || canWrite;

      return {
        isConnected,
        canRead,
        canWrite,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      this.loggingService.logError('❌ Firebase health check failed:', error);

      return {
        isConnected: false,
        canRead: false,
        canWrite: false,
        error: errorMessage,
      };
    }
  }
}
